import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  BellRing,
  Crown,
  MessageSquare,
  Send,
  UserMinus,
  UserX,
} from 'lucide-react';
// Direct imports to avoid circular dependency through the components barrel
import { AppButton } from '../AppButton';
import { colors, fonts, fontWeights, radius, spacing } from '@/theme/tokens';
import type { FeedItem } from './types';

// ---------------------------------------------------------------------------
// Severity styles
// ---------------------------------------------------------------------------

const severityStyles = {
  info: {
    border: colors.accent,
    iconBg: colors.accentLighter,
    iconColor: colors.accent,
  },
  warning: {
    border: colors.warning,
    iconBg: '#FDF5E0',
    iconColor: colors.warning,
  },
  critical: {
    border: colors.danger,
    iconBg: '#FCEAEA',
    iconColor: colors.danger,
  },
} as const;

// ---------------------------------------------------------------------------
// Icon
// ---------------------------------------------------------------------------

function FeedIcon({ type, color, isVip }: { type: string; color: string; isVip?: boolean }) {
  if (isVip) return <Crown size={18} color={color} />;
  if (type === 'lapsed_client') return <UserMinus size={18} color={color} />;
  if (type === 'slow_day') return <BellRing size={18} color={color} />;
  if (type === 'unread_message') return <MessageSquare size={18} color={color} />;
  if (type === 'unmatched_message') return <UserX size={18} color={color} />;
  if (type === 'suggested_message') return <Send size={18} color={color} />;
  return <AlertTriangle size={18} color={color} />;
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

const lineClampStyle = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
} as React.CSSProperties;

interface AssistantFeedCardProps {
  item: FeedItem;
  isBusy: boolean;
  onPrimaryAction: () => void;
  onDismiss?: () => void | Promise<void>;
  onSnooze?: () => void | Promise<void>;
}

export function AssistantFeedCard({
  item,
  isBusy,
  onPrimaryAction,
  onDismiss,
  onSnooze,
}: AssistantFeedCardProps) {
  const { t: rawT } = useTranslation(['admin']);
  const style = severityStyles[item.severity];
  const iconOffset = `calc(36px + ${spacing[3]})`;
  const t = (key: string) => rawT(key as never) as unknown as string;

  return (
    <div
      style={{
        border: `1px solid ${colors.border}`,
        borderLeftWidth: '4px',
        borderLeftColor: style.border,
        borderRadius: radius.lg,
        padding: spacing[4],
        backgroundColor: colors.surface,
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[3],
        boxShadow: '0 10px 28px rgba(31, 41, 51, 0.08)',
      }}
    >
      {/* Top row: icon + title */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: spacing[3] }}>
        {/* Type icon in colored circle */}
        <div
          style={{
            flexShrink: 0,
            width: 36,
            height: 36,
            borderRadius: radius.full,
            backgroundColor: style.iconBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <FeedIcon
            type={item.type}
            color={style.iconColor}
            isVip={(item.raw as { isVip?: boolean } | null)?.isVip}
          />
        </div>

        {/* Title + description */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: fonts.body,
              fontSize: '16px',
              fontWeight: fontWeights.bold,
              color: colors.textPrimary,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: '22px',
            }}
          >
            {item.title}
          </div>
          <div
            style={{
              fontFamily: fonts.body,
              fontSize: '14px',
              color: colors.textSecondary,
              lineHeight: '20px',
              marginTop: '2px',
              ...lineClampStyle,
            }}
          >
            {item.description}
          </div>
        </div>
      </div>

      {/* Relative timestamp */}
      <div
        style={{
          fontSize: '12px',
          color: colors.textMuted,
          paddingLeft: iconOffset,
        }}
      >
        {dayjs(item.timestamp).fromNow()}
      </div>

      {/* CTA + secondary actions */}
      <div
        style={{
          display: 'flex',
          gap: spacing[2],
          flexWrap: 'wrap',
          alignItems: 'center',
          paddingLeft: iconOffset,
        }}
      >
        <AppButton size="sm" variant="primary" onClick={onPrimaryAction} disabled={isBusy}>
          {item.ctaLabel}
        </AppButton>
        {onDismiss && (
          <button
            type="button"
            onClick={() => {
              void onDismiss();
            }}
            disabled={isBusy}
            style={{
              border: 'none',
              background: 'none',
              padding: 0,
              cursor: isBusy ? 'not-allowed' : 'pointer',
              color: colors.textMuted,
              fontFamily: fonts.body,
              fontSize: '13px',
              fontWeight: fontWeights.medium,
              textDecoration: 'underline',
              textUnderlineOffset: '3px',
              opacity: isBusy ? 0.6 : 1,
            }}
          >
            {t('admin:assistantFeed.card.dismiss')}
          </button>
        )}
        {onSnooze && (
          <button
            type="button"
            onClick={() => {
              void onSnooze();
            }}
            disabled={isBusy}
            style={{
              border: 'none',
              background: 'none',
              padding: 0,
              cursor: isBusy ? 'not-allowed' : 'pointer',
              color: colors.textMuted,
              fontFamily: fonts.body,
              fontSize: '13px',
              fontWeight: fontWeights.medium,
              textDecoration: 'underline',
              textUnderlineOffset: '3px',
              opacity: isBusy ? 0.6 : 1,
            }}
          >
            {t('admin:assistantFeed.card.snooze')}
          </button>
        )}
      </div>
    </div>
  );
}
